import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiClientError, apiRequest } from '../api/client.js';
import { readCollection } from '../api/collections.js';
import { useMetaIntegration } from '../system/status.js';
import type { Campaign, PhoneOption, TemplateOption } from './model.js';

type Props = {
  readonly accountId: string; readonly accountName: string; readonly csrfToken: string | null;
  readonly onCreated: (campaign: Campaign) => void; readonly disabled: boolean;
};
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const CampaignComposer = ({ accountId, accountName, csrfToken, onCreated, disabled }: Props) => {
  const { enabled, checkEnabled } = useMetaIntegration();
  const [assets, setAssets] = useState<{ templates: readonly TemplateOption[]; phones: readonly PhoneOption[] } | null>(null);
  const [assetError, setAssetError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [audience, setAudience] = useState('selected');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lock = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    let active = true;
    setAssets(null); setAssetError(false);
    const base = '/api/v1/business-accounts/' + encodeURIComponent(accountId);
    void Promise.all([readCollection<TemplateOption>(base + '/templates'), readCollection<PhoneOption>(base + '/phones')])
      .then(([templates, phones]) => {
        if (active) setAssets({
          templates: templates.filter((item) => item.businessAccountId === accountId && item.status === 'APPROVED'),
          phones: phones.filter((item) => item.businessAccountId === accountId && item.status === 'connected'),
        });
      }).catch(() => { if (active) setAssetError(true); });
    return () => { active = false; };
  }, [accountId, revision]);
  const ready = enabled === true && csrfToken !== null && assets !== null && assets.templates.length > 0 && assets.phones.length > 0 && !busy && !disabled;

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ready || lock.current || assets === null) return;
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    const templateId = String(data.get('templateId') ?? '');
    const phoneNumberId = String(data.get('phoneNumberId') ?? '');
    if (!assets.templates.some((item) => item.id === templateId) || !assets.phones.some((item) => item.id === phoneNumberId)) {
      setError('Selecione template e número válidos desta BM.'); return;
    }
    const contactIds = [...new Set(String(data.get('contactIds') ?? '').split(/[\s,;]+/).filter(Boolean))];
    if (audience === 'selected' && (contactIds.length === 0 || contactIds.some((id) => !uuid.test(id)))) {
      setError('Informe IDs UUID válidos de contatos desta BM, separados por linha ou vírgula.'); return;
    }
    lock.current = true; setBusy(true); setError(null);
    try {
      if (!await checkEnabled()) { if (mounted.current) setError('A integração Meta não está habilitada. Criação bloqueada.'); return; }
      if (!mounted.current) return;
      const result = await apiRequest<{ data: Campaign }>('/api/v1/business-accounts/' + encodeURIComponent(accountId) + '/campaigns', {
        method: 'POST', csrfToken,
        body: { name: String(data.get('name') ?? '').trim(), templateId, phoneNumberId, audienceDefinition: audience === 'all' ? {} : { contactIds } },
      });
      if (typeof result?.data?.id !== 'string' || result.data.businessAccountId !== accountId || result.data.state !== 'draft') throw new Error('Invalid draft response');
      if (mounted.current) { form.reset(); setAudience('selected'); onCreated(result.data); }
    } catch (cause) {
      if (mounted.current) setError(cause instanceof ApiClientError && (cause.status === 401 || cause.status === 403)
        ? 'Sessão sem autorização ou CSRF válido. Entre novamente.'
        : 'A API não confirmou a criação. Atualize a lista antes de tentar novamente para evitar duplicação.');
    } finally { lock.current = false; if (mounted.current) setBusy(false); }
  };

  return <form className="workflow-form" aria-label="Criar rascunho de campanha" onSubmit={(event) => void create(event)}>
    <h2>Novo rascunho</h2>
    <p>BM: {accountName}. Criar e validar não envia mensagens. O agendamento exige uma confirmação separada.</p>
    {enabled === true ? null : <p>Criação bloqueada: integração Meta desativada ou ainda não confirmada.</p>}
    {csrfToken === null ? <p>Entre novamente para obter uma sessão com CSRF e liberar alterações.</p> : null}
    {assetError ? <p role="alert">Não foi possível consultar templates e números desta BM.</p>
      : assets === null ? <p>Consultando templates e números…</p>
      : assets.templates.length === 0 || assets.phones.length === 0 ? <p>Sincronize a BM: é necessário um template aprovado e um número conectado para criar um rascunho.</p> : null}
    <fieldset disabled={!ready}>
      <legend className="field-label">Dados da campanha</legend>
      <label>Nome da campanha<input name="name" required maxLength={200}/></label>
      <label>Template aprovado<select name="templateId" required defaultValue=""><option value="">Selecione um template</option>{assets?.templates.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.language})</option>)}</select></label>
      <label>Número de envio<select name="phoneNumberId" required defaultValue=""><option value="">Selecione um número</option>{assets?.phones.map((item) => <option key={item.id} value={item.id}>{item.maskedDisplayNumber || item.verifiedName} — {item.verifiedName}</option>)}</select></label>
      <label>Público<select value={audience} onChange={(event) => setAudience(event.target.value)}><option value="selected">Contatos específicos por ID</option><option value="all">Todos os contatos desta BM, sujeitos à validação</option></select></label>
      {audience === 'selected' ? <label>IDs dos contatos<textarea name="contactIds" rows={3} required aria-describedby="audience-help"/></label> : null}
      <p id="audience-help">A API verifica consentimento, supressão, variáveis e limites. Contatos de outras BMs não fazem parte deste público.</p>
    </fieldset>
    <button type="submit" disabled={!ready}>{busy ? 'Criando rascunho…' : 'Criar rascunho'}</button>
    <button type="button" disabled={busy || disabled} onClick={() => setRevision((value) => value + 1)}>Atualizar templates e números</button>
    {error === null ? null : <p role="alert" className="form-error">{error}</p>}
  </form>;
};
